import { useEffect, useState } from "react";
import type { ParallaxCameraConfig, ParallaxMappingConfig, SceneNodeInfo } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { useCaps } from "../../CapsProvider";
import { CompactCheck, CompactNumber, CompactSlider, CompactText, MiniBtn, Row } from "../../inspector/rows";

/** Object Rotations — radial duplication of the selected part. */
export function ObjectRotationsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const groupId = useDomainState("selection") as string | null;
  const [rotations, setRotations] = useState(6);
  const [aroundParent, setAroundParent] = useState(true);
  const [off, setOff] = useState({ x: 0, y: 0, z: 0 });
  const [skips, setSkips] = useState("");
  const [busy, setBusy] = useState(false);
  if (!groupId) return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">Select a part to radially duplicate.</span>;
  const apply = async () => {
    setBusy(true);
    try {
      const tree = await r.getSceneTree();
      let nodeId: string | null = null;
      const walk = (n: SceneNodeInfo) => { if (!nodeId && n.groupId === groupId) nodeId = n.id; n.children.forEach(walk); };
      tree.forEach(walk);
      if (nodeId) await r.applyObjectRotations(nodeId, { rotations, rotateAroundParent: aroundParent, offset: off, skips });
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col">
      <Row label="Rotation Count"><CompactSlider value={rotations} min={1} max={100} step={1} onChange={setRotations} /></Row>
      <Row label="Rotate Around Parent"><CompactCheck checked={aroundParent} onChange={setAroundParent} /></Row>
      <Row label="Offset" stacked>
        <div className="flex gap-1">
          {(["x", "y", "z"] as const).map((ax) => <CompactNumber key={ax} value={off[ax]} step={0.001} width={56} onChange={(v) => setOff({ ...off, [ax]: v })} />)}
        </div>
      </Row>
      <Row label="Rotation Skips"><CompactText value={skips} placeholder="e.g. 2, 5" onChange={setSkips} /></Row>
      <div className="px-3 py-1.5"><MiniBtn primary icon="rotate_right" onClick={() => void apply()}>{busy ? "Duplicating…" : "Duplicate"}</MiniBtn></div>
    </div>
  );
}

/** Extrude Tube Shapes — generate a circular tube/band. */
export function ExtrudeTubeControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const [enabled, setEnabled] = useState(true);
  const [shapeSeg, setShapeSeg] = useState(32);
  const [tubeSeg, setTubeSeg] = useState(64);
  const [sx, setSx] = useState(1);
  const [sy, setSy] = useState(1);
  const [splits, setSplits] = useState("0.3, 0.6");
  const [hSplits, setHSplits] = useState(true);
  const run = () => {
    // v31: enabled/extrudeCirceTube are new optional fields on
    // ExtrudeTubeOptions — omit them for a pre-v31 viewer whose implementation
    // doesn't know them (extra fields would just be ignored, but this keeps
    // the call shape identical to before v31 for older viewers).
    const opts = {
      shapeSegments: shapeSeg,
      tubularSegments: tubeSeg,
      shapeScale: { x: sx, y: sy },
      materialSplits: splits,
      horizontalSplits: hSplits,
      ...(caps.v31 ? { enabled, extrudeCirceTube: true } : {}),
    };
    void r.extrudeTube(opts);
  };
  return (
    <div className="flex flex-col">
      {caps.v31 ? <Row label="Enabled"><CompactCheck checked={enabled} onChange={setEnabled} /></Row> : null}
      <Row label="Shape Segments (X)"><CompactSlider value={shapeSeg} min={1} max={100} step={1} onChange={setShapeSeg} /></Row>
      <Row label="Tube Segments (Y)"><CompactSlider value={tubeSeg} min={1} max={100} step={1} onChange={setTubeSeg} /></Row>
      <Row label="Shape Scale" stacked>
        <div className="flex gap-1"><CompactNumber value={sx} step={0.01} width={56} onChange={setSx} /><CompactNumber value={sy} step={0.01} width={56} onChange={setSy} /></div>
      </Row>
      <Row label="Material Splits"><CompactText value={splits} onChange={setSplits} /></Row>
      <Row label="Horizontal Splits"><CompactCheck checked={hSplits} onChange={setHSplits} /></Row>
      <div className="px-3 py-1.5"><MiniBtn primary icon="all_inclusive" onClick={run}>Extrude Circle Tube</MiniBtn></div>
    </div>
  );
}

/** Simplify Modifier — decimate meshes. */
export function SimplifyControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const [factor, setFactor] = useState(0.5);
  const [errorThreshold, setErrorThreshold] = useState(0.5);
  const [lockBorder, setLockBorder] = useState(false);
  const [result, setResult] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selection = useDomainState("selection");
  const run = async (selectedOnly: boolean) => {
    // v31: simplifyMeshes gained an opts arg (errorThreshold/lockBorder, the
    // meshoptimizer path). A pre-v31 viewer's implementation only reads the
    // first two positional args, so passing the extra opts is harmless, but
    // only send it once it's meaningful to avoid implying support that isn't
    // there yet.
    setBusy(true);
    setError(null);
    try {
      const res = caps.v31
        ? await r.simplifyMeshes(factor, selectedOnly, { errorThreshold, lockBorder })
        : await r.simplifyMeshes(factor, selectedOnly);
      setResult(`${res.before.toLocaleString()} → ${res.after.toLocaleString()} tris`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Simplify failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col">
      <Row label="Simplify Factor"><CompactSlider value={factor} min={0} max={1} step={0.01} onChange={setFactor} /></Row>
      {caps.v31 ? (
        <>
          <Row label="Error Threshold"><CompactNumber value={errorThreshold} min={0} step={0.01} width={64} onChange={setErrorThreshold} /></Row>
          <Row label="Lock Border"><CompactCheck checked={lockBorder} onChange={setLockBorder} /></Row>
        </>
      ) : null}
      <div className="flex flex-wrap gap-1.5 px-3 py-1.5">
        {/* Guarded: the viewer falls back to simplifying EVERY group when
            selectedOnly is true but nothing is selected, so an unguarded
            "Simplify Selected" silently decimated the whole model — and
            simplification is not undoable. */}
        <MiniBtn
          primary
          icon="compress"
          busy={busy}
          disabled={!selection}
          onClick={() => void run(true)}
        >
          {busy ? "Simplifying…" : "Simplify Selected"}
        </MiniBtn>
        <MiniBtn icon="compress" busy={busy} onClick={() => void run(false)}>
          {busy ? "Simplifying…" : "Simplify All"}
        </MiniBtn>
      </div>
      {!selection ? (
        <span className="px-3 pb-1.5 text-[11px] text-ed-text-3">Select a part to simplify it on its own.</span>
      ) : null}
      {error ? <span className="px-3 pb-1.5 text-[11px] text-ed-red">{error}</span> : null}
      {result ? <span className="px-3 pb-1.5 text-[11px] text-ed-text-3">{result}</span> : null}
    </div>
  );
}

/** Parallax mapping + parallax camera (stored configs). */
export function ParallaxControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [m, setM] = useState<ParallaxMappingConfig | null>(null);
  const [cam, setCam] = useState<ParallaxCameraConfig | null>(null);
  useEffect(() => { r.getParallaxMapping().then(setM).catch(() => {}); r.getParallaxCamera().then(setCam).catch(() => {}); }, [r]);
  const sm = (p: Partial<ParallaxMappingConfig>) => r.setParallaxMapping(p).then(setM);
  const sc = (p: Partial<ParallaxCameraConfig>) => r.setParallaxCamera(p).then(setCam);
  return (
    <div className="flex flex-col">
      <div className="px-3 py-1 text-[12px] font-semibold text-ed-text-2">Parallax mapping</div>
      {m ? (<>
        <Row label="Enabled"><CompactCheck checked={m.enabled} onChange={(v) => void sm({ enabled: v })} /></Row>
        <Row label="Step count"><CompactSlider value={m.stepCount} min={1} max={32} step={1} onChange={(v) => void sm({ stepCount: v })} /></Row>
        <Row label="Binary search steps"><CompactSlider value={m.binaryStepCount} min={1} max={8} step={1} onChange={(v) => void sm({ binaryStepCount: v })} /></Row>
      </>) : null}
      <div className="px-3 py-1 text-[12px] font-semibold text-ed-text-2">Parallax camera</div>
      {cam ? (<>
        <Row label="Enabled"><CompactCheck checked={cam.enabled} onChange={(v) => void sc({ enabled: v })} /></Row>
        <Row label="Invert"><CompactCheck checked={cam.invert} onChange={(v) => void sc({ invert: v })} /></Row>
        <Row label="Sensitivity"><CompactSlider value={cam.sensitivity} min={0.1} max={2} step={0.01} onChange={(v) => void sc({ sensitivity: v })} /></Row>
        <Row label="Damping"><CompactSlider value={cam.damping} min={0} max={1} step={0.001} onChange={(v) => void sc({ damping: v })} /></Row>
      </>) : null}
    </div>
  );
}
